import { PvpFlag, flagStateOf, flagXpPercent, levelRow, type Level, type XpRates } from "odyssey-shared";
import type { TipLine, TipTone, XpTip } from "../bar/describe.ts";
import type { HudProgress } from "./store.ts";
import { say } from "../../words.ts";

/**
 * The XP bar's tooltip (P2.4-T8, the author's ruling of 2026-10-02, ADR-083's note of that date): the level, the XP
 * into it, and the rate a kill's XP is multiplied by with each factor it comes from. The factors are the ones the map
 * server applies (ADR-083 decision 3): the flag modifier, read through the shared `flagXpPercent` the server calls and
 * split into the flag's own part and the Hostile zone lock's part, whose product is always the server's percent; no
 * effect, boost or world rate changes XP yet, so each reads none. The level gap is per kill, by the creature's level,
 * so it is a note, not a factor of the current rate. Every word is a draft for the author. Pure.
 */

/**
 * The rate's factors in whole percents: the product (`percent`), the flag's own part and its state's name, the Hostile
 * zone lock's part, and the boosts' and the world's (100 while none exists).
 */
export interface XpRate {
  readonly percent: number;
  readonly flag: { readonly state: string; readonly percent: number };
  readonly zone: number;
  readonly boosts: number;
  readonly world: number;
}

/**
 * A flag state's name as the tooltip writes it.
 *
 * @param state The flag's state, one of `PvpFlag`'s three.
 * @returns The name; Protected for a state the page does not know.
 */
function flagStateName(state: number): string {
  return state === PvpFlag.Flagged ? say("hud.xp.states.flagged") : state === PvpFlag.Combatant ? say("hud.xp.states.combatant") : say("hud.xp.states.protected");
}

/**
 * The XP rate for a flag byte.
 *
 * @param flag The player's flag byte.
 * @param rates The rules' XP rates, the ones the map server applies.
 * @returns The rate and its factors.
 * @example
 * xpRate(PvpFlag.Flagged | PvpFlag.ZoneLocked, xpRatesOf(PAGE_RULES)).zone;
 * // 120
 */
export function xpRate(flag: number, rates: XpRates): XpRate {
  const percent = flagXpPercent(flag, rates);
  const own = flagXpPercent(flag & ~PvpFlag.ZoneLocked, rates);
  return { percent, flag: { state: flagStateName(flagStateOf(flag)), percent: own }, zone: Math.round((percent * 100) / own), boosts: 100, world: 100 };
}

/**
 * A percent as a multiplier: "x1.0", "x0.6", "x1.25".
 *
 * @param percent Whole percents.
 * @returns The text.
 */
function times(percent: number): string {
  return `x${(percent / 100).toFixed(percent % 10 === 0 ? 1 : 2)}`;
}

/**
 * A whole number with its thousands grouped by commas.
 *
 * @param value The number.
 * @returns The text.
 */
function grouped(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * The tone of the current rate: a benefit above 1, plain at 1. No rate is below 1: safety is never a penalty, and the
 * rules refuse a Protected rate under 100 (ADR-083's amendment of 2026-10-07, A1).
 *
 * @param percent The rate.
 * @returns The tone.
 */
function rateTone(percent: number): TipTone {
  return percent > 100 ? "good" : "plain";
}

/**
 * A factor's line: its multiplier, x1.0 for one that changes nothing (the author's ruling of 2026-10-03).
 *
 * @param label What it comes from.
 * @param percent Its percent.
 * @returns The line.
 */
function factorLine(label: string, percent: number): TipLine {
  return { text: say("hud.xp.factor", { label, times: times(percent) }), tone: "plain" };
}

/**
 * The XP bar's tooltip.
 *
 * @param levels The page's level table.
 * @param progress The player's level and XP total.
 * @param flag The player's flag byte.
 * @param rates The rules' XP rates.
 * @returns The tooltip.
 * @example
 * xpTip(levels, { level: 2, xp: 190 }, PvpFlag.Flagged, rates).typeLine;
 * // "XP 60 / 195"
 */
export function xpTip(levels: readonly Level[], progress: HudProgress, flag: number, rates: XpRates): XpTip {
  const last = levels[levels.length - 1] as Level;
  const capped = progress.level >= last.level;
  const row = levelRow(levels, progress.level);
  const typeLine = capped ? say("hud.xp.maxLevel", { xp: grouped(progress.xp) }) : say("hud.xp.progress", { into: grouped(Math.max(0, progress.xp - row.xp)), span: grouped(levelRow(levels, progress.level + 1).xp - row.xp) });
  const rate = xpRate(flag, rates);
  const lines: readonly TipLine[] = [
    { text: say("hud.xp.rate", { times: times(rate.percent) }), tone: rateTone(rate.percent) },
    { text: say("hud.xp.fromFlag", { state: rate.flag.state, times: times(rate.flag.percent) }), tone: "plain" },
    rate.zone === 100 ? factorLine(say("hud.xp.fromZone"), 100) : { text: say("hud.xp.fromHostile", { times: times(rate.zone) }), tone: "plain" },
    factorLine(say("hud.xp.fromBoosts"), rate.boosts),
    factorLine(say("hud.xp.fromWorld"), rate.world),
  ];
  return { kind: "xp", name: say("hud.xp.level", { level: String(progress.level) }), typeLine, lines };
}
